/**
 * Load more functionality for collection product grids.
 * Fetches the next page of products via Section Rendering API and appends to grid.
 */
class LoadMore extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.load-more-button');
    this.nextPage = parseInt(this.dataset.nextPage || '2', 10);
    this.nextUrl = this.dataset.nextUrl || '';
    this.sectionId = this.dataset.sectionId;

    if (this.button && this.sectionId) {
      this.button.addEventListener('click', this.onClick.bind(this));
    }
  }

  buildFetchUrl() {
    // Prefer Shopify's own paginate.next.url — it's the canonical next-page URL
    // including any active filters/sort and the correct page number.
    if (this.nextUrl) {
      const separator = this.nextUrl.includes('?') ? '&' : '?';
      return `${this.nextUrl}${separator}section_id=${encodeURIComponent(this.sectionId)}`;
    }

    // Fallback: construct URL manually from current location + nextPage.
    const params = new URLSearchParams(window.location.search);
    params.set('page', this.nextPage);
    return `${window.location.pathname}?section_id=${encodeURIComponent(this.sectionId)}&${params.toString()}`;
  }

  async onClick() {
    if (this.loading) return;
    this.setLoading(true);

    try {
      const url = this.buildFetchUrl();
      const response = await fetch(url, { cache: 'no-store' });
      const html = await response.text();

      if (!html) {
        this.setHasMore(false);
        return;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // With ?section_id=, the response is the section's HTML directly (no outer body wrapping the section as a child of #shopify-section-...).
      // Find the product grid wherever it lives in the parsed doc.
      const newProductGrid = doc.getElementById('product-grid');
      const currentProductGrid = document.getElementById('product-grid');
      let newItemsCount = 0;

      if (newProductGrid && currentProductGrid) {
        const newItems = Array.from(newProductGrid.querySelectorAll(':scope > li'));
        newItemsCount = newItems.length;
        newItems.forEach((item) => {
          item.classList.add('scroll-trigger--cancel');
          currentProductGrid.appendChild(item);
        });
      }

      const newLoadMoreWrapper = doc.querySelector('load-more.load-more-wrapper') ||
        doc.querySelector('load-more') ||
        doc.querySelector('.load-more-wrapper');

      if (newLoadMoreWrapper) {
        const nextPage = parseInt(
          newLoadMoreWrapper.dataset?.nextPage || newLoadMoreWrapper.getAttribute('data-next-page') || '0',
          10
        );
        const nextUrl = newLoadMoreWrapper.dataset?.nextUrl || newLoadMoreWrapper.getAttribute('data-next-url') || '';
        const hasMore =
          (newLoadMoreWrapper.dataset?.hasMore || newLoadMoreWrapper.getAttribute('data-has-more')) === 'true';

        if (nextPage > 0) this.nextPage = nextPage;
        this.nextUrl = nextUrl;
        this.setHasMore(hasMore);
      } else {
        // No load-more wrapper in response means we've reached the last page.
        this.setHasMore(false);

        // Defensive fallback: if we somehow received a full page of items but no wrapper,
        // increment the page counter so the next click attempts a fresh page.
        const perPage = parseInt(this.dataset.perPage || '16', 10);
        if (newItemsCount >= perPage) {
          this.nextPage += 1;
          this.nextUrl = '';
        }
      }
    } catch (error) {
      console.error('Load more error:', error);
      // Don't hide on error - user can retry
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    this.loading = loading;
    if (this.button) {
      this.button.disabled = loading;
      this.button.classList.toggle('is-loading', loading);
    }
  }

  setHasMore(hasMore) {
    this.dataset.hasMore = hasMore ? 'true' : 'false';
    if (!hasMore) {
      this.style.display = 'none';
    }
  }
}

customElements.define('load-more', LoadMore);
